import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppShell } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ExternalLink, Plus, Pencil, Trash2, Link2, Star } from "lucide-react";
import { VaultDocuments } from "@/components/vault-documents";

export const Route = createFileRoute("/_authenticated/vault")({
  head: () => ({
    meta: [
      { title: "Vault — Boardroom" },
      { name: "description", content: "Your organized library of important links and resources." },
    ],
  }),
  component: VaultPage,
});

type VaultLink = {
  id: string;
  title: string;
  url: string;
  category: string;
  notes: string | null;
  is_favorite: boolean;
  created_at: string;
};

const DEFAULT_CATEGORIES = [
  "Contracts",
  "Brand",
  "Templates",
  "Financial",
  "Clients",
  "Tools",
  "Learning",
  "Other",
];

function normalizeUrl(input: string) {
  const trimmed = input.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function hostFromUrl(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function VaultPage() {
  const { user } = useAuth();
  const [links, setLinks] = useState<VaultLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("All");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<VaultLink | null>(null);
  const [open, setOpen] = useState(false);

  async function load() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("vault_links")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setLinks((data as VaultLink[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [user?.id]);

  const categoriesInUse = Array.from(
    new Set([...DEFAULT_CATEGORIES, ...links.map((l) => l.category)])
  );

  const filtered = links.filter((l) => {
    if (filter === "★ Favorites") {
      if (!l.is_favorite) return false;
    } else if (filter !== "All" && l.category !== filter) {
      return false;
    }
    if (search) {
      const s = search.toLowerCase();
      return (
        l.title.toLowerCase().includes(s) ||
        l.url.toLowerCase().includes(s) ||
        (l.notes ?? "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  async function toggleFavorite(l: VaultLink) {
    // optimistic
    setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, is_favorite: !x.is_favorite } : x)));
    const { error } = await supabase
      .from("vault_links")
      .update({ is_favorite: !l.is_favorite })
      .eq("id", l.id);
    if (error) {
      toast.error(error.message);
      setLinks((prev) => prev.map((x) => (x.id === l.id ? { ...x, is_favorite: l.is_favorite } : x)));
    }
  }


  async function handleDelete(id: string) {
    if (!confirm("Delete this link?")) return;
    const { error } = await supabase.from("vault_links").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Deleted");
    load();
  }

  function openNew() {
    setEditing(null);
    setOpen(true);
  }
  function openEdit(link: VaultLink) {
    setEditing(link);
    setOpen(true);
  }

  const grouped = filtered.reduce<Record<string, VaultLink[]>>((acc, l) => {
    (acc[l.category] ||= []).push(l);
    return acc;
  }, {});

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Vault</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Your CEO link library. Paste Google Drive, Dropbox, Notion, Figma, or any URL — files stay wherever you keep them.
          </p>
        </div>
        <Button onClick={openNew} className="gap-2">
          <Plus className="h-4 w-4" /> Add link
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Input
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-64"
        />
        <div className="flex flex-wrap gap-1">
          {["All", "★ Favorites", ...categoriesInUse].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                filter === c
                  ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                  : "border-[color:var(--hairline)] text-muted-foreground hover:text-foreground"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[color:var(--hairline)] rounded-lg p-10 text-center">
          <Link2 className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {links.length === 0
              ? "No links yet. Add your first one."
              : "No links match this filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <section key={cat}>
              <h2 className="text-xs uppercase tracking-wider text-[color:var(--gold-dim)] mb-2">
                {cat} <span className="text-muted-foreground">({items.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((l) => (
                  <div
                    key={l.id}
                    className="group border border-[color:var(--hairline)] rounded-lg p-4 bg-[color:var(--surface)] hover:border-[color:var(--gold-dim)] transition"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <a
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sm hover:text-[color:var(--gold)] transition inline-flex items-center gap-1.5 min-w-0"
                      >
                        <span className="truncate">{l.title}</span>
                        <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                      </a>
                      <div className="flex gap-0.5 shrink-0 items-center">
                        <button
                          onClick={() => toggleFavorite(l)}
                          className={`p-1 transition ${
                            l.is_favorite
                              ? "text-[color:var(--gold)]"
                              : "text-muted-foreground opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:text-[color:var(--gold)]"
                          }`}
                          aria-label={l.is_favorite ? "Unstar" : "Star"}
                        >
                          <Star className="h-3.5 w-3.5" fill={l.is_favorite ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={() => openEdit(l)}
                          className="p-1 text-muted-foreground hover:text-foreground opacity-100 md:opacity-0 md:group-hover:opacity-100 transition"
                          aria-label="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>

                        <button
                          onClick={() => handleDelete(l.id)}
                          className="p-1 text-muted-foreground hover:text-destructive opacity-100 md:opacity-0 md:group-hover:opacity-100 transition"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{hostFromUrl(l.url)}</p>
                    {l.notes && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{l.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <LinkDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        categories={categoriesInUse}
        onSaved={load}
        userId={user?.id}
      />

      <VaultDocuments />
    </AppShell>
  );
}

function LinkDialog({
  open,
  onOpenChange,
  editing,
  categories,
  onSaved,
  userId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: VaultLink | null;
  categories: string[];
  onSaved: () => void;
  userId?: string;
}) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("Other");
  const [customCategory, setCustomCategory] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(editing?.title ?? "");
      setUrl(editing?.url ?? "");
      setCategory(editing?.category && categories.includes(editing.category) ? editing.category : editing?.category ?? "Other");
      setCustomCategory("");
      setNotes(editing?.notes ?? "");
    }
  }, [open, editing, categories]);

  async function save() {
    if (!userId) return;
    const finalCategory = category === "__custom__" ? customCategory.trim() : category;
    if (!title.trim() || !url.trim() || !finalCategory) {
      toast.error("Title, URL, and category are required");
      return;
    }
    setSaving(true);
    const payload = {
      user_id: userId,
      title: title.trim(),
      url: normalizeUrl(url),
      category: finalCategory,
      notes: notes.trim() || null,
    };
    const { error } = editing
      ? await supabase.from("vault_links").update(payload).eq("id", editing.id)
      : await supabase.from("vault_links").insert(payload);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success(editing ? "Updated" : "Added");
    onOpenChange(false);
    onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit link" : "Add link"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="v-title">Title</Label>
            <Input
              id="v-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Client contract template"
            />
          </div>
          <div>
            <Label htmlFor="v-url">URL</Label>
            <Input
              id="v-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/…"
            />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
                <SelectItem value="__custom__">+ New category…</SelectItem>
              </SelectContent>
            </Select>
            {category === "__custom__" && (
              <Input
                className="mt-2"
                placeholder="Category name"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
              />
            )}
          </div>
          <div>
            <Label htmlFor="v-notes">Notes (optional)</Label>
            <Textarea
              id="v-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything you want to remember about this link"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
